/**
 * AP-47 — **Montageprotokoll** (Baustein `SITE_DOCS.ASSEMBLY`): was Gerät, ERP und Server über die
 * Art gleich wissen müssen — Umfang, Bauteile mit Detailgrad und Prüfliste, Fachtabellen nach
 * EN 1090-2, Mängel, Abnahme, die Prüfregeln vor der Unterschrift, die Freigabe nach Umfang (RN-1)
 * und vor allem der **kanonische Fachteil** (`montageFachteil`), den alle Seiten mit derselben
 * Funktion bauen (AP-46 6.).
 *
 * Spezifikation: docs/Arbeitspakete/AP-47/AP-47_Montageprotokoll.md
 */
import { festeZahl, kanonischerText } from './kanonisch'
import { siteDocFreigabeSchnitt, type SiteDocFreigabe, type SiteDocRelease, type SiteDocSignerRole } from './site-docs'

/** Fassung des Fachteils. Eine Änderung am Aufbau unten ist eine neue Fassung (AP-46 6.). */
export const MONTAGE_SCHEMA_VERSION = 1

export const MONTAGE_UMFAENGE = ['MONTAGE', 'ABNAHME', 'MONTAGE_ABNAHME'] as const
export type MontageUmfang = (typeof MONTAGE_UMFAENGE)[number]

export const MONTAGE_DETAILGRADE = ['EINFACH', 'MESSWERTE', 'NACHWEIS'] as const
export type MontageDetailgrad = (typeof MONTAGE_DETAILGRADE)[number]

export const MONTAGE_EXC = ['EXC1', 'EXC2', 'EXC3', 'EXC4'] as const
export type MontageExc = (typeof MONTAGE_EXC)[number]

export const MONTAGE_PUNKT_ARTEN = ['PRUEFEN', 'MESSEN', 'TEXT'] as const
export type MontagePunktArt = (typeof MONTAGE_PUNKT_ARTEN)[number]

/** i. O. · n. i. O. · entfällt */
export const MONTAGE_ERGEBNISSE = ['OK', 'NOK', 'NA'] as const
export type MontageErgebnis = (typeof MONTAGE_ERGEBNISSE)[number]

export const MONTAGE_TABELLEN_PFLICHT = ['PFLICHT', 'OPTIONAL', 'AUS'] as const
export type MontageTabellenPflicht = (typeof MONTAGE_TABELLEN_PFLICHT)[number]

/** Lage X · Lage Y · Höhe · Neigung */
export const MONTAGE_RICHTUNGEN = ['X', 'Y', 'HOEHE', 'NEIGUNG'] as const
export type MontageRichtung = (typeof MONTAGE_RICHTUNGEN)[number]

/** Anziehverfahren nach EN 1090-2; „anderes" steht als freier Text in `verfahren`. */
export const MONTAGE_VERFAHREN = ['DREHMOMENT', 'KOMBINIERT', 'HRC', 'DTI'] as const
export type MontageVerfahren = (typeof MONTAGE_VERFAHREN)[number]

export const MONTAGE_GEWICHTE = ['WESENTLICH', 'UNWESENTLICH'] as const
export type MontageGewicht = (typeof MONTAGE_GEWICHTE)[number]

export const MONTAGE_ABNAHME_ERGEBNISSE = ['ABGENOMMEN', 'VORBEHALT', 'NICHT_ABGENOMMEN'] as const
export type MontageAbnahmeErgebnis = (typeof MONTAGE_ABNAHME_ERGEBNISSE)[number]

/** Zustand eines Mangels im Büro (MO-8, 6.3): offen · behoben (Nachabnahme) · behoben laut Büro. */
export const MONTAGE_MANGEL_ZUSTAENDE = ['OPEN', 'RESOLVED', 'RESOLVED_OFFICE'] as const
export type MontageMangelZustand = (typeof MONTAGE_MANGEL_ZUSTAENDE)[number]

/** Grenzen der Vorlagen (5.1) — `pruefeExtra` prüft sie. */
export const MONTAGE_VORLAGEN_MAX = 30
export const MONTAGE_PUNKTE_MAX = 60
/** Grenzen des Inhalts — gegen versehentlich riesige Protokolle. */
export const MONTAGE_BAUTEILE_MAX = 50
export const MONTAGE_MAENGEL_MAX = 200
export const MONTAGE_ZEILEN_MAX = 200

// ── Bezeichnungen (Oberfläche und PDF) ──────────────────────────────────────

export const MONTAGE_UEBERSCHRIFT: Record<MontageUmfang, string> = {
  MONTAGE: 'Montageprotokoll',
  ABNAHME: 'Abnahmeprotokoll',
  MONTAGE_ABNAHME: 'Montage- und Abnahmeprotokoll',
}

export const MONTAGE_UMFANG_TEXT: Record<MontageUmfang, string> = {
  MONTAGE: 'Montage',
  ABNAHME: 'Abnahme',
  MONTAGE_ABNAHME: 'Montage und Abnahme',
}

export const MONTAGE_DETAILGRAD_TEXT: Record<MontageDetailgrad, string> = {
  EINFACH: 'Einfach',
  MESSWERTE: 'Messwerte',
  NACHWEIS: 'Nachweis',
}

export const MONTAGE_ERGEBNIS_TEXT: Record<MontageErgebnis, string> = { OK: 'i. O.', NOK: 'n. i. O.', NA: 'entfällt' }

export const MONTAGE_RICHTUNG_TEXT: Record<MontageRichtung, string> = { X: 'Lage X', Y: 'Lage Y', HOEHE: 'Höhe', NEIGUNG: 'Neigung' }

export const MONTAGE_VERFAHREN_TEXT: Record<MontageVerfahren, string> = {
  DREHMOMENT: 'Drehmomentverfahren',
  KOMBINIERT: 'kombiniertes Verfahren',
  HRC: 'HRC-Verfahren',
  DTI: 'direkte Kraftanzeiger',
}

export const MONTAGE_GEWICHT_TEXT: Record<MontageGewicht, string> = { WESENTLICH: 'wesentlich', UNWESENTLICH: 'unwesentlich' }

export const MONTAGE_ABNAHME_TEXT: Record<MontageAbnahmeErgebnis, string> = {
  ABGENOMMEN: 'abgenommen',
  VORBEHALT: 'abgenommen mit Vorbehalt',
  NICHT_ABGENOMMEN: 'nicht abgenommen',
}

export const MONTAGE_ZUSTAND_TEXT: Record<MontageMangelZustand, string> = {
  OPEN: 'offen',
  RESOLVED: 'behoben',
  RESOLVED_OFFICE: 'behoben laut Büro',
}

/** Verfahren als Text — ein Schlüssel aus der Liste oder der freie Text für „anderes". */
export function montageVerfahrenText(v: string): string {
  return (MONTAGE_VERFAHREN_TEXT as Record<string, string>)[v] ?? v
}

/** Enthält der Umfang die Montage (Prüfliste) bzw. die Abnahme (Ergebnis, Unterschrift des Kunden)? */
export const montageMitMontage = (u: MontageUmfang): boolean => u !== 'ABNAHME'
export const montageMitAbnahme = (u: MontageUmfang): boolean => u !== 'MONTAGE'

// ── Bestätigungstexte und Einstellungen (3.2, 5.1, 11.2) ────────────────────

/** Bestätigungstext der Abnahme — steht im `confirmationText` der Einstellung, je Mandant änderbar (MO-22). */
export const MONTAGE_BESTAETIGUNG_ABNAHME_VORGABE = 'Ich bestätige die Abnahme der oben angeführten Bauteile mit dem angegebenen Ergebnis am {Datum}.'
/** Bestätigungstext der Montage mit Unterschrift — steht in `extra.textMontage` (MO-22). */
export const MONTAGE_BESTAETIGUNG_MONTAGE_VORGABE = 'Ich bestätige die Montage der oben angeführten Bauteile wie protokolliert.'

/** Ein Punkt einer Prüflisten-Vorlage. */
export interface MontageVorlagePunkt {
  /** stabil innerhalb der Vorlage */
  key: string
  /** Zwischenüberschrift („Befestigung") */
  gruppe: string | null
  text: string
  art: MontagePunktArt
  /** bei `MESSEN` */
  einheit: string | null
  /** optional; sonst am Bauteil eintragen */
  soll: string | null
  tolMinus: string | null
  tolPlus: string | null
  /** nur bei `MESSWERTE` und `NACHWEIS` wirksam */
  fotoPflicht: boolean
  /** Bei `TEXT`: muss eine Angabe haben (4.3, MO-25). */
  pflicht: boolean
}

export interface MontageVorlageTabellen {
  anziehprotokoll: MontageTabellenPflicht
  vermessung: MontageTabellenPflicht
}

/** Eine Prüflisten-Vorlage (5.1) — im `extra` der Einstellung, kommt mit der Gerätekonfiguration aufs Gerät. */
export interface MontageVorlage {
  /** UUID, vom Server vergeben */
  id: string
  name: string
  detailgrad: MontageDetailgrad
  /** vom Server bei jeder Änderung hochgezählt */
  fassung: number
  aktiv: boolean
  sortOrder: number
  punkte: MontageVorlagePunkt[]
  /** nur `NACHWEIS` */
  tabellen: MontageVorlageTabellen
}

/** Einstellungen der Art in `SiteDocumentSetting.extra` (5.1, 11.2). */
export interface MontageExtra {
  vorlagen: MontageVorlage[]
  /** Detailgrad, wenn die Ausführungsklasse unbekannt ist (4.2). */
  detailgradVorgabe: MontageDetailgrad
  /** Umfang beim Anlegen (MO-20). */
  umfangVorgabe: MontageUmfang
  /** Bestätigungstext der Montage mit Unterschrift; die Abnahme nutzt `confirmationText` (MO-22). */
  textMontage: string | null
}

/**
 * Vorgabe des `extra` **ohne** `vorlagen` — fehlt der Schlüssel, gelten die Startvorlagen
 * (`montageExtra`); sie stehen damit in der Vorgabe, bis der Mandant eigene speichert.
 */
export const MONTAGE_EXTRA_VORGABE: Omit<MontageExtra, 'vorlagen'> = {
  detailgradVorgabe: 'EINFACH',
  umfangVorgabe: 'MONTAGE_ABNAHME',
  textMontage: MONTAGE_BESTAETIGUNG_MONTAGE_VORGABE,
}

const istEines = <T extends string>(liste: readonly T[], v: unknown): v is T => typeof v === 'string' && (liste as readonly string[]).includes(v)

/**
 * Das `extra` lesend in feste Form bringen — fehlende Teile aus der Vorgabe, Unbekanntes fällt weg.
 * **Fehlt `vorlagen` ganz** (der Mandant hat die Vorlagen nie gespeichert), gelten die Startvorlagen
 * (MO-17); eine bewusst geleerte Liste bleibt leer.
 */
export function montageExtra(extra: unknown): MontageExtra {
  const e = (extra ?? {}) as Partial<MontageExtra>
  return {
    vorlagen:
      e.vorlagen === undefined
        ? MONTAGE_STARTVORLAGEN.map((v) => montageVorlageNormal(v))
        : Array.isArray(e.vorlagen)
          ? e.vorlagen.filter((v): v is MontageVorlage => !!v && typeof v === 'object' && typeof v.id === 'string' && Array.isArray(v.punkte)).map((v) => montageVorlageNormal(v))
          : [],
    detailgradVorgabe: istEines(MONTAGE_DETAILGRADE, e.detailgradVorgabe) ? e.detailgradVorgabe : MONTAGE_EXTRA_VORGABE.detailgradVorgabe,
    umfangVorgabe: istEines(MONTAGE_UMFAENGE, e.umfangVorgabe) ? e.umfangVorgabe : MONTAGE_EXTRA_VORGABE.umfangVorgabe,
    textMontage: typeof e.textMontage === 'string' ? e.textMontage : e.textMontage === null ? null : MONTAGE_EXTRA_VORGABE.textMontage,
  }
}

/**
 * RN-2 — **Prüft das `extra` der Art** beim Speichern der Einstellungen: Aufbau und Grenzen
 * (höchstens 30 Vorlagen, je Vorlage höchstens 60 Punkte, eindeutige Schlüssel). Liefert die
 * Fehlerliste — leer heißt gültig. `id` und `fassung` vergibt der Server, nicht der Aufrufer.
 */
export function montagePruefeExtra(extra: unknown): string[] {
  const f: string[] = []
  if (extra == null) return f
  if (typeof extra !== 'object' || Array.isArray(extra)) return ['extra']
  const e = extra as Record<string, unknown>
  for (const k of Object.keys(e)) if (!['vorlagen', 'detailgradVorgabe', 'umfangVorgabe', 'textMontage'].includes(k)) f.push(`extra.${k}`)
  if (e.detailgradVorgabe !== undefined && !istEines(MONTAGE_DETAILGRADE, e.detailgradVorgabe)) f.push('detailgradVorgabe')
  if (e.umfangVorgabe !== undefined && !istEines(MONTAGE_UMFAENGE, e.umfangVorgabe)) f.push('umfangVorgabe')
  if (e.textMontage !== undefined && e.textMontage !== null && (typeof e.textMontage !== 'string' || e.textMontage.length > 1000)) f.push('textMontage')
  if (e.vorlagen === undefined) return f
  if (!Array.isArray(e.vorlagen)) return [...f, 'vorlagen']
  if (e.vorlagen.length > MONTAGE_VORLAGEN_MAX) f.push('vorlagen.max')
  const ids = new Set<string>()
  e.vorlagen.forEach((roh, i) => {
    const v = roh as Record<string, unknown>
    const p = `vorlagen.${i}`
    if (!v || typeof v !== 'object' || Array.isArray(v)) return f.push(p)
    if (typeof v.id === 'string') {
      if (ids.has(v.id)) f.push(`${p}.id`)
      ids.add(v.id)
    }
    if (typeof v.name !== 'string' || !v.name.trim() || v.name.length > 200) f.push(`${p}.name`)
    if (!istEines(MONTAGE_DETAILGRADE, v.detailgrad)) f.push(`${p}.detailgrad`)
    if (v.aktiv !== undefined && typeof v.aktiv !== 'boolean') f.push(`${p}.aktiv`)
    if (v.sortOrder !== undefined && !Number.isInteger(v.sortOrder)) f.push(`${p}.sortOrder`)
    const t = (v.tabellen ?? {}) as Record<string, unknown>
    if (v.tabellen !== undefined && (typeof v.tabellen !== 'object' || v.tabellen === null)) f.push(`${p}.tabellen`)
    else {
      if (t.anziehprotokoll !== undefined && !istEines(MONTAGE_TABELLEN_PFLICHT, t.anziehprotokoll)) f.push(`${p}.tabellen.anziehprotokoll`)
      if (t.vermessung !== undefined && !istEines(MONTAGE_TABELLEN_PFLICHT, t.vermessung)) f.push(`${p}.tabellen.vermessung`)
    }
    if (!Array.isArray(v.punkte)) return f.push(`${p}.punkte`)
    if (v.punkte.length > MONTAGE_PUNKTE_MAX) f.push(`${p}.punkte.max`)
    const keys = new Set<string>()
    v.punkte.forEach((rohP, j) => {
      const q = rohP as Record<string, unknown>
      const pp = `${p}.punkte.${j}`
      if (!q || typeof q !== 'object' || Array.isArray(q)) return f.push(pp)
      if (typeof q.key !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(q.key) || keys.has(q.key)) f.push(`${pp}.key`)
      else keys.add(q.key)
      if (typeof q.text !== 'string' || !q.text.trim() || q.text.length > 500) f.push(`${pp}.text`)
      if (!istEines(MONTAGE_PUNKT_ARTEN, q.art)) f.push(`${pp}.art`)
      if (q.gruppe != null && (typeof q.gruppe !== 'string' || q.gruppe.length > 200)) f.push(`${pp}.gruppe`)
      if (q.einheit != null && (typeof q.einheit !== 'string' || q.einheit.length > 20)) f.push(`${pp}.einheit`)
      for (const z of ['soll', 'tolMinus', 'tolPlus'] as const) {
        if (q[z] != null && (typeof q[z] !== 'string' || (String(q[z]).trim() && montageZahl(q[z] as string) === null))) f.push(`${pp}.${z}`)
      }
      if (q.fotoPflicht !== undefined && typeof q.fotoPflicht !== 'boolean') f.push(`${pp}.fotoPflicht`)
      if (q.pflicht !== undefined && typeof q.pflicht !== 'boolean') f.push(`${pp}.pflicht`)
    })
  })
  return f
}

/** Eine Vorlage in feste Form — Grundlage für Schnappschuss und Speichern. */
export function montageVorlageNormal(v: Partial<MontageVorlage> & { id: string }): MontageVorlage {
  const t = (v.tabellen ?? {}) as Partial<MontageVorlageTabellen>
  return {
    id: v.id,
    name: (v.name ?? '').trim(),
    detailgrad: istEines(MONTAGE_DETAILGRADE, v.detailgrad) ? v.detailgrad : 'EINFACH',
    fassung: Number.isInteger(v.fassung) && (v.fassung as number) > 0 ? (v.fassung as number) : 1,
    aktiv: v.aktiv !== false,
    sortOrder: Number.isInteger(v.sortOrder) ? (v.sortOrder as number) : 0,
    tabellen: {
      anziehprotokoll: istEines(MONTAGE_TABELLEN_PFLICHT, t.anziehprotokoll) ? t.anziehprotokoll : 'OPTIONAL',
      vermessung: istEines(MONTAGE_TABELLEN_PFLICHT, t.vermessung) ? t.vermessung : 'OPTIONAL',
    },
    punkte: (v.punkte ?? []).map((p) => ({
      key: p.key,
      gruppe: p.gruppe?.trim() || null,
      text: (p.text ?? '').trim(),
      art: istEines(MONTAGE_PUNKT_ARTEN, p.art) ? p.art : 'PRUEFEN',
      einheit: p.art === 'MESSEN' ? p.einheit?.trim() || null : null,
      soll: p.art === 'MESSEN' ? p.soll?.trim() || null : null,
      tolMinus: p.art === 'MESSEN' ? p.tolMinus?.trim() || null : null,
      tolPlus: p.art === 'MESSEN' ? p.tolPlus?.trim() || null : null,
      fotoPflicht: !!p.fotoPflicht,
      pflicht: p.art === 'TEXT' ? !!p.pflicht : false,
    })),
  }
}

// ── Inhalt, wie Gerät und ERP ihn führen ────────────────────────────────────

export interface MontagePunkt {
  key: string
  gruppe: string | null
  text: string
  art: MontagePunktArt
  ergebnis: MontageErgebnis | null
  /** Ergebnis von Hand gegen die Rechnung gesetzt (MO-7) — Bemerkung Pflicht. */
  ueberschrieben: boolean
  messwert: string | null
  einheit: string | null
  soll: string | null
  tolMinus: string | null
  tolPlus: string | null
  angabe: string | null
  bemerkung: string | null
  /** `clientId`s der Fotos des Dokuments */
  fotos: string[]
  // aus der Vorlage — Prüfregeln, nicht im Fachteil
  fotoPflicht: boolean
  pflicht: boolean
}

export interface MontageAnziehZeile {
  key: string
  verbindung: string
  garnitur: string
  anzahl: number | null
  /** Schlüssel aus `MONTAGE_VERFAHREN` oder freier Text („anderes") */
  verfahren: string
  sollMoment: string | null
  pruefmittel: string | null
  kalibriertBis: string | null
  geprueft: number | null
  ergebnis: 'OK' | 'NOK' | null
  bemerkung: string | null
}

export interface MontageVermessZeile {
  key: string
  punkt: string
  richtung: MontageRichtung
  soll: string | null
  ist: string | null
  einheit: string
  toleranz: string | null
  ergebnis: 'OK' | 'NOK' | null
  ueberschrieben: boolean
  bemerkung: string | null
}

export interface MontageBauteil {
  key: string
  sortOrder: number
  bezeichnung: string
  menge: string | null
  exc: MontageExc | null
  detailgrad: MontageDetailgrad
  /** Schnappschuss der Vorlage beim Wählen (MO-6); `id` ist Büro-Wissen und steht nicht im Fachteil. */
  vorlage: { id: string | null; name: string; fassung: number } | null
  /**
   * **Alle** Punkte der Vorlage — auch die, die der Detailgrad gerade ausblendet (Messen bei
   * „Einfach"). So holt ein Wechsel nach oben die Punkte aus dem Schnappschuss, nicht aus der
   * heutigen Vorlage (MO-6, MO-18). Gezählt werden nur die aktiven (`montageAktivePunkte`).
   */
  punkte: MontagePunkt[]
  tabellen: MontageVorlageTabellen
  anziehprotokoll: MontageAnziehZeile[]
  vermessung: MontageVermessZeile[]
  /** Hinweis vom Gerät: Position und Arbeitsschritt aus der Mappe — Zuordnung, kein Inhalt (MO-21). */
  positionId?: string | null
  workStepId?: string | null
}

export interface MontageMangel {
  nr: number
  bauteilKey: string | null
  text: string
  /** Pflicht vor der Unterschrift, nie von der KI (MO-15) — im Entwurf leer erlaubt. */
  gewicht: MontageGewicht | null
  frist: string | null
  fotos: string[]
  // Nur in Antworten des Servers (ERP): Kennung und Bürostand (6.2)
  id?: string
  state?: MontageMangelZustand
  resolvedAt?: string | null
  resolvedNote?: string | null
  resolvedByNumber?: string | null
  projectTaskId?: string | null
  taskDoneAt?: string | null
}

export interface MontageBezug {
  nummer: string
  pruefwert: string
}

export interface MontageBehoben {
  nummer: string
  nr: number
  text: string
}

export interface MontageAbnahme {
  ergebnis: MontageAbnahmeErgebnis | null
  teilabnahme: boolean
  /** Vorbehalt bzw. Grund */
  vorbehalt: string | null
  /** getrennte Abnahme — Montageprotokolle, auf die sie sich bezieht (MO-3) */
  bezug: MontageBezug[]
  /** Nachabnahme — abgehakte Mängel früherer Protokolle */
  behoben: MontageBehoben[]
}

/** Der Fachinhalt, wie er übertragen wird — Kopf-`content` samt Mängeln. */
export interface MontageInhalt {
  umfang: MontageUmfang
  zeitraum: { von: string; bis: string } | null
  ausfuehrende: string[]
  bauteile: MontageBauteil[]
  maengel: MontageMangel[]
  abnahme: MontageAbnahme | null
  bemerkung: string | null
}

/** Ein leerer Inhalt für einen neuen Entwurf. */
export function montageLeer(umfang: MontageUmfang): MontageInhalt {
  return {
    umfang,
    zeitraum: null,
    ausfuehrende: [],
    bauteile: [],
    maengel: [],
    abnahme: montageMitAbnahme(umfang) ? montageAbnahmeLeer() : null,
    bemerkung: null,
  }
}

export function montageAbnahmeLeer(): MontageAbnahme {
  return { ergebnis: null, teilabnahme: false, vorbehalt: null, bezug: [], behoben: [] }
}

// ── Detailgrad und Vorlage (4.2, 5.1) ───────────────────────────────────────

/** MO-4: `EXC1` → Einfach, `EXC2` → Messwerte, `EXC3`/`EXC4` → Nachweis; ohne Klasse die Vorgabe des Mandanten. */
export function montageDetailgradVorschlag(exc: MontageExc | null | undefined, vorgabe: MontageDetailgrad): MontageDetailgrad {
  if (exc === 'EXC1') return 'EINFACH'
  if (exc === 'EXC2') return 'MESSWERTE'
  if (exc === 'EXC3' || exc === 'EXC4') return 'NACHWEIS'
  return vorgabe
}

/** `EXC2` aus „EXC 2", „exc2" — sonst `null`. */
export function montageExcAus(text: string | null | undefined): MontageExc | null {
  const m = /^\s*EXC\s*([1-4])\s*$/i.exec(text ?? '')
  return m ? (`EXC${m[1]}` as MontageExc) : null
}

const RANG: Record<MontageDetailgrad, number> = { EINFACH: 0, MESSWERTE: 1, NACHWEIS: 2 }

/** Ist ein Punkt beim Detailgrad aktiv? „Einfach" kennt kein Messen (4.2). */
export function montagePunktAktiv(p: Pick<MontagePunkt, 'art'>, detailgrad: MontageDetailgrad): boolean {
  return p.art !== 'MESSEN' || detailgrad !== 'EINFACH'
}

export function montageAktivePunkte<T extends Pick<MontagePunkt, 'art'>>(b: { detailgrad: MontageDetailgrad; punkte: readonly T[] }): T[] {
  return b.punkte.filter((p) => montagePunktAktiv(p, b.detailgrad))
}

/** Fotopflicht wirkt nur bei Messwerte und Nachweis (4.3). */
export function montageFotoPflicht(p: Pick<MontagePunkt, 'fotoPflicht'>, detailgrad: MontageDetailgrad): boolean {
  return p.fotoPflicht && detailgrad !== 'EINFACH'
}

/** Ein Punkt aus der Vorlage, ohne Ergebnis. */
export function montagePunktAusVorlage(v: MontageVorlagePunkt): MontagePunkt {
  return {
    key: v.key,
    gruppe: v.gruppe,
    text: v.text,
    art: v.art,
    ergebnis: null,
    ueberschrieben: false,
    messwert: null,
    einheit: v.einheit,
    soll: v.soll,
    tolMinus: v.tolMinus,
    tolPlus: v.tolPlus,
    angabe: null,
    bemerkung: null,
    fotos: [],
    fotoPflicht: v.fotoPflicht,
    pflicht: v.pflicht,
  }
}

/** Schnappschuss einer Vorlage ins Bauteil (MO-6) — die Punkte werden kopiert, Ergebnisse stehen leer. */
export function montageVorlageAnwenden<B extends MontageBauteil>(b: B, v: MontageVorlage | null): B {
  if (!v) return { ...b, vorlage: null, punkte: [], tabellen: { anziehprotokoll: 'OPTIONAL', vermessung: 'OPTIONAL' } }
  return {
    ...b,
    vorlage: { id: v.id, name: v.name, fassung: v.fassung },
    punkte: v.punkte.map(montagePunktAusVorlage),
    tabellen: { ...v.tabellen },
  }
}

/** Ein neues Bauteil — Detailgrad aus der Klasse, ohne Vorlage. */
export function montageBauteilNeu(key: string, sortOrder: number, bezeichnung: string, exc: MontageExc | null, vorgabe: MontageDetailgrad): MontageBauteil {
  return {
    key,
    sortOrder,
    bezeichnung,
    menge: null,
    exc,
    detailgrad: montageDetailgradVorschlag(exc, vorgabe),
    vorlage: null,
    punkte: [],
    tabellen: { anziehprotokoll: 'OPTIONAL', vermessung: 'OPTIONAL' },
    anziehprotokoll: [],
    vermessung: [],
  }
}

/** Was beim Wechsel des Detailgrads verloren ginge (MO-18) — leer heißt: keine Rückfrage nötig. */
export function montageVerlustBeiWechsel(b: MontageBauteil, neu: MontageDetailgrad): string[] {
  if (RANG[neu] >= RANG[b.detailgrad]) return []
  const verlust: string[] = []
  if (neu === 'EINFACH' && b.punkte.some((p) => p.art === 'MESSEN' && (p.messwert || p.ergebnis || p.bemerkung || p.fotos.length))) verlust.push('messwerte')
  if (b.detailgrad === 'NACHWEIS' && (b.anziehprotokoll.length || b.vermessung.length)) verlust.push('tabellen')
  return verlust
}

/** Detailgrad wechseln (nur im Entwurf): nach unten fallen Messwerte bzw. Fachtabellen weg. */
export function montageDetailgradWechseln(b: MontageBauteil, neu: MontageDetailgrad): MontageBauteil {
  const punkte =
    neu === 'EINFACH'
      ? b.punkte.map((p) => (p.art === 'MESSEN' ? { ...p, ergebnis: null, ueberschrieben: false, messwert: null, bemerkung: null, fotos: [] } : p))
      : b.punkte
  return { ...b, detailgrad: neu, punkte, anziehprotokoll: neu === 'NACHWEIS' ? b.anziehprotokoll : [], vermessung: neu === 'NACHWEIS' ? b.vermessung : [] }
}

// ── Rechnen (4.3, 4.4, MO-7) ────────────────────────────────────────────────

/** Eine Zahl aus der Eingabe — Komma oder Punkt, Ränder egal; unlesbar → `null`. */
export function montageZahl(text: string | number | null | undefined): number | null {
  if (text == null) return null
  if (typeof text === 'number') return Number.isFinite(text) ? text : null
  const t = text.trim().replace(/\s/g, '').replace(',', '.')
  if (!t || !/^[+-]?(\d+\.?\d*|\.\d+)$/.test(t)) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** Tausendstel als ganze Zahl — über `festeZahl`, damit Grenzwerte auf jedem Gerät gleich fallen. */
function milli(n: number): number {
  return Number(festeZahl(n, 3).replace('.', ''))
}

/** Zahl als Text mit drei Nachkommastellen für den Fachteil — unlesbar oder leer → `null`. */
export function montageZahlText(text: string | number | null | undefined): string | null {
  const n = montageZahl(text)
  return n === null ? null : festeZahl(n, 3)
}

/**
 * Ergebnis eines Messwerts: innerhalb `soll − tolMinus … soll + tolPlus` (Grenzen eingeschlossen)
 * i. O., sonst n. i. O. Toleranzen zählen als Betrag; eine fehlende Seite ist 0. Ohne Messwert oder
 * Soll gibt es nichts zu rechnen → `null`.
 */
export function montageMessErgebnis(p: Pick<MontagePunkt, 'messwert' | 'soll' | 'tolMinus' | 'tolPlus'>): 'OK' | 'NOK' | null {
  const ist = montageZahl(p.messwert)
  const soll = montageZahl(p.soll)
  if (ist === null || soll === null) return null
  const minus = Math.abs(montageZahl(p.tolMinus) ?? 0)
  const plus = Math.abs(montageZahl(p.tolPlus) ?? 0)
  const i = milli(ist)
  return i >= milli(soll) - milli(minus) && i <= milli(soll) + milli(plus) ? 'OK' : 'NOK'
}

/** Abweichung Ist − Soll als Text (drei Stellen) — ohne beide Werte `null`. */
export function montageAbweichung(z: Pick<MontageVermessZeile, 'soll' | 'ist'>): string | null {
  const ist = montageZahl(z.ist)
  const soll = montageZahl(z.soll)
  if (ist === null || soll === null) return null
  const d = milli(ist) - milli(soll)
  return festeZahl(d / 1000, 3)
}

/** Ergebnis einer Vermessung: |Abweichung| ≤ Toleranz → i. O. */
export function montageVermessErgebnis(z: Pick<MontageVermessZeile, 'soll' | 'ist' | 'toleranz'>): 'OK' | 'NOK' | null {
  const abw = montageAbweichung(z)
  const tol = montageZahl(z.toleranz)
  if (abw === null || tol === null) return null
  return Math.abs(Number(abw.replace('.', ''))) <= milli(Math.abs(tol)) ? 'OK' : 'NOK'
}

/** Das geltende Ergebnis eines Punkts: bei `MESSEN` gerechnet, außer überschrieben oder „entfällt". */
export function montagePunktErgebnis(p: Pick<MontagePunkt, 'art' | 'ergebnis' | 'ueberschrieben' | 'messwert' | 'soll' | 'tolMinus' | 'tolPlus'>): MontageErgebnis | null {
  if (p.art === 'TEXT') return null
  if (p.art !== 'MESSEN' || p.ueberschrieben || p.ergebnis === 'NA') return p.ergebnis
  return montageMessErgebnis(p) ?? p.ergebnis
}

/** Das geltende Ergebnis einer Vermessungszeile. */
export function montageVermessGeltend(z: MontageVermessZeile): 'OK' | 'NOK' | null {
  if (z.ueberschrieben) return z.ergebnis
  return montageVermessErgebnis(z) ?? z.ergebnis
}

// ── Kanonischer Fachteil (9.1) ──────────────────────────────────────────────

const leer = (t: string | null | undefined): string | null => {
  const k = t == null ? '' : kanonischerText(String(t))
  return k ? k : null
}

const datum = (t: string | null | undefined): string | null => {
  const k = leer(t)
  return k && /^\d{4}-\d{2}-\d{2}/.test(k) ? k.slice(0, 10) : null
}

const ganz = (n: unknown): number | null => {
  const z = typeof n === 'number' ? n : montageZahl(n as string)
  return z === null || !Number.isFinite(z) ? null : Math.trunc(z)
}

/** Fachliche Reihenfolge der Bauteile: `sortOrder`, bei Gleichstand der Schlüssel. */
export function montageSortiert<T extends { sortOrder: number; key: string }>(liste: readonly T[]): T[] {
  return [...liste].sort((a, b) => a.sortOrder - b.sortOrder || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
}

/** Toleranz als Betrag mit drei Stellen — das Vorzeichen zählt nicht. */
const betragText = (t: string | null | undefined): string | null => {
  const n = montageZahl(t)
  return n === null ? null : festeZahl(Math.abs(n), 3)
}

/** Foto-Verweise: eindeutig und sortiert — die Reihenfolge der Eingabe zählt nicht. */
const sortiertText = (liste: readonly string[]): string[] => [...new Set(liste)].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))

/**
 * **Der kanonische Fachteil**, `schemaVersion` 1 (9.1). Die Abweichung der Vermessung und ein nicht
 * überschriebenes Ergebnis bei `MESSEN`/Vermessung **rechnet** diese Funktion aus Soll, Ist und
 * Toleranz — der Server prüft beim Eintreffen, dass Gerät und Server dasselbe rechnen.
 *
 * Nicht darin: die Kennung der Vorlage, Position und Arbeitsschritt (Zuordnung, BD-24), Foto- und
 * Angabepflicht aus der Vorlage (Prüfregeln) und die Punkte, die der Detailgrad ausblendet. Beim
 * Umfang `ABNAHME` tragen die Bauteile nur ihre Bezeichnung (3.1).
 */
export function montageFachteil(inhalt: MontageInhalt): Record<string, unknown> {
  const mitMontage = montageMitMontage(inhalt.umfang)
  const a = inhalt.abnahme
  return {
    umfang: inhalt.umfang,
    zeitraum: inhalt.zeitraum && datum(inhalt.zeitraum.von) && datum(inhalt.zeitraum.bis) ? { von: datum(inhalt.zeitraum.von), bis: datum(inhalt.zeitraum.bis) } : null,
    ausfuehrende: inhalt.ausfuehrende.map((n) => leer(n)).filter((n): n is string => !!n),
    bauteile: montageSortiert(inhalt.bauteile).map((b) => ({
      key: b.key,
      bezeichnung: kanonischerText(b.bezeichnung ?? ''),
      menge: leer(b.menge),
      exc: b.exc ?? null,
      detailgrad: b.detailgrad,
      vorlage: mitMontage && b.vorlage ? { name: kanonischerText(b.vorlage.name), fassung: b.vorlage.fassung } : null,
      punkte: mitMontage
        ? montageAktivePunkte(b).map((p) => ({
            key: p.key,
            gruppe: leer(p.gruppe),
            text: kanonischerText(p.text),
            art: p.art,
            ergebnis: montagePunktErgebnis(p),
            ueberschrieben: p.art === 'MESSEN' ? !!p.ueberschrieben && p.ergebnis !== 'NA' : false,
            messwert: p.art === 'MESSEN' ? montageZahlText(p.messwert) : null,
            einheit: p.art === 'MESSEN' ? leer(p.einheit) : null,
            soll: p.art === 'MESSEN' ? montageZahlText(p.soll) : null,
            tolMinus: p.art === 'MESSEN' ? betragText(p.tolMinus) : null,
            tolPlus: p.art === 'MESSEN' ? betragText(p.tolPlus) : null,
            angabe: p.art === 'TEXT' ? leer(p.angabe) : null,
            bemerkung: leer(p.bemerkung),
            fotos: sortiertText(p.fotos ?? []),
          }))
        : [],
      anziehprotokoll:
        mitMontage && b.detailgrad === 'NACHWEIS'
          ? b.anziehprotokoll.map((z) => ({
              key: z.key,
              verbindung: kanonischerText(z.verbindung ?? ''),
              garnitur: kanonischerText(z.garnitur ?? ''),
              anzahl: ganz(z.anzahl) ?? 0,
              verfahren: kanonischerText(z.verfahren ?? ''),
              sollMoment: montageZahlText(z.sollMoment),
              pruefmittel: leer(z.pruefmittel),
              kalibriertBis: datum(z.kalibriertBis),
              geprueft: ganz(z.geprueft),
              ergebnis: z.ergebnis ?? null,
              bemerkung: leer(z.bemerkung),
            }))
          : [],
      vermessung:
        mitMontage && b.detailgrad === 'NACHWEIS'
          ? b.vermessung.map((z) => ({
              key: z.key,
              punkt: kanonischerText(z.punkt ?? ''),
              richtung: z.richtung,
              soll: montageZahlText(z.soll),
              ist: montageZahlText(z.ist),
              einheit: leer(z.einheit) ?? 'mm',
              toleranz: betragText(z.toleranz),
              abweichung: montageAbweichung(z),
              ergebnis: montageVermessGeltend(z),
              ueberschrieben: !!z.ueberschrieben,
              bemerkung: leer(z.bemerkung),
            }))
          : [],
    })),
    maengel: [...inhalt.maengel]
      .sort((x, y) => x.nr - y.nr)
      .map((m) => ({ nr: m.nr, bauteilKey: m.bauteilKey ?? null, text: kanonischerText(m.text ?? ''), gewicht: m.gewicht ?? null, frist: datum(m.frist), fotos: sortiertText(m.fotos ?? []) })),
    abnahme:
      montageMitAbnahme(inhalt.umfang) && a
        ? {
            ergebnis: a.ergebnis ?? null,
            teilabnahme: !!a.teilabnahme,
            vorbehalt: leer(a.vorbehalt),
            bezug: [...a.bezug].sort((x, y) => (x.nummer < y.nummer ? -1 : x.nummer > y.nummer ? 1 : 0)).map((z) => ({ nummer: kanonischerText(z.nummer), pruefwert: z.pruefwert })),
            behoben: [...a.behoben]
              .sort((x, y) => (x.nummer < y.nummer ? -1 : x.nummer > y.nummer ? 1 : x.nr - y.nr))
              .map((z) => ({ nummer: kanonischerText(z.nummer), nr: z.nr, text: kanonischerText(z.text ?? '') })),
          }
        : null,
    bemerkung: leer(inhalt.bemerkung),
  }
}

// ── Pflichtangaben (9.2) und Ergebnis-Regel (7.1) ───────────────────────────

/**
 * Welche Abnahme-Ergebnisse die Mängel des Protokolls zulassen (7.1, MO-9). `vorbehalt` ist der
 * Text für Vorbehalt bzw. Grund.
 */
export function montageErgebnisZulaessig(ergebnis: MontageAbnahmeErgebnis, maengel: readonly Pick<MontageMangel, 'gewicht'>[], vorbehalt: string | null): boolean {
  const wesentlich = maengel.some((m) => m.gewicht === 'WESENTLICH')
  const unwesentlich = maengel.some((m) => m.gewicht === 'UNWESENTLICH')
  const text = !!leer(vorbehalt)
  if (ergebnis === 'ABGENOMMEN') return maengel.length === 0
  if (ergebnis === 'VORBEHALT') return !wesentlich && (unwesentlich || text)
  return wesentlich || text
}

/**
 * Pflicht vor „Zur Unterschrift" bzw. Abschluss (9.2). `fotos` sind die `clientId`s der Fotos am
 * Dokument — ein Verweis auf ein fehlendes Foto ist ein Fehler. Die Freigabe passend zum Umfang
 * prüft der Rahmen über `montageFreigabe` (RN-1).
 */
export function montagePruefe(inhalt: MontageInhalt, fotos: readonly string[] | null = null): string[] {
  const f: string[] = []
  const vorhanden = fotos ? new Set(fotos) : null
  const fehltFoto = (liste: readonly string[]) => !!vorhanden && liste.some((c) => !vorhanden.has(c))
  if (!inhalt.bauteile.length) f.push('parts')
  const z = inhalt.zeitraum
  if (z && (!datum(z.von) || !datum(z.bis) || datum(z.von)! > datum(z.bis)!)) f.push('period')
  const mitMontage = montageMitMontage(inhalt.umfang)
  for (const b of montageSortiert(inhalt.bauteile)) {
    const pk = `part:${b.key}`
    if (!leer(b.bezeichnung)) f.push(`${pk}:name`)
    if (!mitMontage) continue
    const maengelAmBauteil = inhalt.maengel.some((m) => m.bauteilKey === b.key)
    for (const p of montageAktivePunkte(b)) {
      const k = `point:${b.key}:${p.key}`
      const erg = montagePunktErgebnis(p)
      if (p.art !== 'TEXT') {
        if (!erg) f.push(`${k}:result`)
        if (p.art === 'MESSEN' && erg !== 'NA' && montageZahl(p.messwert) === null) f.push(`${k}:value`)
        if (erg === 'NOK' && !leer(p.bemerkung) && !maengelAmBauteil) f.push(`${k}:nok`)
        if (p.art === 'MESSEN' && p.ueberschrieben && erg !== 'NA' && !leer(p.bemerkung)) f.push(`${k}:override`)
        if (montageFotoPflicht(p, b.detailgrad) && erg !== 'NA' && !(p.fotos ?? []).length) f.push(`${k}:photo`)
      } else {
        if (p.pflicht && !leer(p.angabe)) f.push(`${k}:entry`)
        if (montageFotoPflicht(p, b.detailgrad) && !(p.fotos ?? []).length) f.push(`${k}:photo`)
      }
      if (fehltFoto(p.fotos ?? [])) f.push(`${k}:photoMissing`)
    }
    if (b.detailgrad === 'NACHWEIS') {
      if (b.tabellen.anziehprotokoll === 'PFLICHT' && !b.anziehprotokoll.length) f.push(`${pk}:torque`)
      if (b.tabellen.vermessung === 'PFLICHT' && !b.vermessung.length) f.push(`${pk}:survey`)
      for (const r of b.anziehprotokoll) {
        if (!leer(r.verbindung) || !leer(r.verfahren) || !(ganz(r.anzahl) ?? 0) || !r.ergebnis) f.push(`torque:${b.key}:${r.key}:torqueRow`)
        else if (r.ergebnis === 'NOK' && !leer(r.bemerkung) && !maengelAmBauteil) f.push(`torque:${b.key}:${r.key}:nok`)
      }
      for (const r of b.vermessung) {
        const erg = montageVermessGeltend(r)
        if (!leer(r.punkt) || montageZahl(r.soll) === null || montageZahl(r.ist) === null || montageZahl(r.toleranz) === null || !erg) f.push(`survey:${b.key}:${r.key}:surveyRow`)
        else {
          if (r.ueberschrieben && !leer(r.bemerkung)) f.push(`survey:${b.key}:${r.key}:override`)
          if (erg === 'NOK' && !leer(r.bemerkung) && !maengelAmBauteil) f.push(`survey:${b.key}:${r.key}:nok`)
        }
      }
    }
  }
  const keys = new Set(inhalt.bauteile.map((b) => b.key))
  for (const m of inhalt.maengel) {
    const k = `defect:${m.nr}`
    if (!leer(m.text)) f.push(`${k}:text`)
    if (!m.gewicht) f.push(`${k}:weight`)
    if (m.bauteilKey && !keys.has(m.bauteilKey)) f.push(`${k}:part`)
    if (m.frist && !datum(m.frist)) f.push(`${k}:due`)
    if (fehltFoto(m.fotos ?? [])) f.push(`${k}:photoMissing`)
  }
  if (montageMitAbnahme(inhalt.umfang)) {
    const a = inhalt.abnahme
    if (!a?.ergebnis) f.push('acceptance:acceptanceResult')
    else if (!montageErgebnisZulaessig(a.ergebnis, inhalt.maengel, a.vorbehalt)) f.push(`acceptance:${a.ergebnis === 'ABGENOMMEN' ? 'openDefects' : a.ergebnis === 'VORBEHALT' ? 'reservation' : 'reason'}`)
  }
  return f
}

/** Fortschritt eines Bauteils für die Anzeige („12 von 15 Punkten"). */
export function montageFortschritt(b: MontageBauteil): { erledigt: number; gesamt: number; ersterOffen: string | null } {
  const punkte = montageAktivePunkte(b)
  let erledigt = 0
  let ersterOffen: string | null = null
  for (const p of punkte) {
    const fertig = p.art === 'TEXT' ? !p.pflicht || !!leer(p.angabe) : !!montagePunktErgebnis(p) && (p.art !== 'MESSEN' || montagePunktErgebnis(p) === 'NA' || montageZahl(p.messwert) !== null)
    if (fertig) erledigt++
    else ersterOffen ??= p.key
  }
  return { erledigt, gesamt: punkte.length, ersterOffen }
}

/** Ergebnis-Summe eines Bauteils für die eingeklappte Vorzeige. */
export function montageErgebnisSumme(b: MontageBauteil): { ok: number; nok: number; na: number } {
  const s = { ok: 0, nok: 0, na: 0 }
  for (const p of montageAktivePunkte(b)) {
    const e = montagePunktErgebnis(p)
    if (e === 'OK') s.ok++
    else if (e === 'NOK') s.nok++
    else if (e === 'NA') s.na++
  }
  if (b.detailgrad === 'NACHWEIS') {
    for (const r of b.anziehprotokoll) {
      if (r.ergebnis === 'NOK') s.nok++
      else if (r.ergebnis === 'OK') s.ok++
    }
    for (const r of b.vermessung) {
      const e = montageVermessGeltend(r)
      if (e === 'NOK') s.nok++
      else if (e === 'OK') s.ok++
    }
  }
  return s
}

/** Steht irgendwo ein n. i. O.? Dann ist „Arbeitsschritt fertig melden" nicht vorgewählt (MO-13). */
export function montageHatNok(inhalt: MontageInhalt): boolean {
  return montageMitMontage(inhalt.umfang) && inhalt.bauteile.some((b) => montageErgebnisSumme(b).nok > 0)
}

// ── Freigabe nach Umfang (RN-1, 3.2) ────────────────────────────────────────

/**
 * RN-1 — **Freigabe nach Inhalt.** Nur Montage: Abschluss durch den Erfasser oder Unterschrift ohne
 * Pflichtrolle; mit Abnahme: Unterschrift vor Ort oder per Link, Pflicht-Unterzeichner aus der
 * Einstellung (Vorgabe Kunde). Immer als **Schnittmenge** mit der Einstellung des Mandanten — was er
 * für die Art abgeschaltet hat, schaltet der Inhalt nicht wieder ein (MO-2).
 */
export function montageFreigabe(
  inhalt: Pick<MontageInhalt, 'umfang'> | null,
  einstellung: { allowedReleases: readonly SiteDocRelease[]; requiredSigners: readonly SiteDocSignerRole[]; confirmationText: string | null; extra: Record<string, unknown> | null },
): SiteDocFreigabe {
  const umfang = inhalt?.umfang ?? 'MONTAGE_ABNAHME'
  if (!montageMitAbnahme(umfang)) {
    return siteDocFreigabeSchnitt(einstellung, { allowedReleases: ['INTERNAL', 'SIGNATURE'], requiredSigners: [], confirmationText: montageExtra(einstellung.extra).textMontage })
  }
  return siteDocFreigabeSchnitt(einstellung, {
    allowedReleases: ['SIGNATURE', 'LINK'],
    requiredSigners: einstellung.requiredSigners.length ? [...einstellung.requiredSigners] : ['CUSTOMER'],
    confirmationText: einstellung.confirmationText ?? MONTAGE_BESTAETIGUNG_ABNAHME_VORGABE,
  })
}

// ── Startvorlagen (5.2, MO-17) ──────────────────────────────────────────────

type StartPunkt = [key: string, gruppe: string | null, text: string, art: MontagePunktArt, extra?: Partial<MontageVorlagePunkt>]

const startPunkte = (liste: StartPunkt[]): MontageVorlagePunkt[] =>
  liste.map(([key, gruppe, text, art, extra]) => ({ key, gruppe, text, art, einheit: null, soll: null, tolMinus: null, tolPlus: null, fotoPflicht: false, pflicht: false, ...extra }))

/**
 * Drei Startvorlagen **ohne Normzahlen als Soll** — das Soll kommt aus Plan und Bauteil (MO-17). Feste
 * Kennungen: Sie gelten als Vorgabe, solange der Mandant keine eigenen Vorlagen gespeichert hat, und
 * sind dann auf jedem Gerät dieselben (MO-27). Fachliche Durchsicht durch die Schweißaufsicht bzw.
 * WPK des Auftraggebers offen (16.3).
 */
export const MONTAGE_STARTVORLAGEN: readonly MontageVorlage[] = [
  {
    id: '6f1d0a47-0001-4000-8000-000000000001',
    name: 'Metallbau allgemein',
    detailgrad: 'EINFACH',
    fassung: 1,
    aktiv: true,
    sortOrder: 0,
    tabellen: { anziehprotokoll: 'AUS', vermessung: 'AUS' },
    punkte: startPunkte([
      ['lage', 'Lage', 'Lage und Höhe nach Plan', 'PRUEFEN'],
      ['befestigung', 'Befestigung', 'Befestigung und Verankerung', 'PRUEFEN'],
      ['funktion', 'Funktion', 'Funktion (Türen, Tore: Öffnen, Schließen, Verriegeln)', 'PRUEFEN'],
      ['oberflaeche', 'Oberfläche', 'Oberfläche unbeschädigt bzw. ausgebessert', 'PRUEFEN'],
      ['montagehilfen', 'Abschluss', 'Montagehilfen entfernt', 'PRUEFEN'],
      ['reinigung', 'Abschluss', 'Baustelle gereinigt', 'PRUEFEN'],
      ['einweisung', 'Abschluss', 'Kunde in Bedienung und Wartung eingewiesen', 'PRUEFEN'],
    ]),
  },
  {
    id: '6f1d0a47-0002-4000-8000-000000000002',
    name: 'Geländer und Absturzsicherung',
    detailgrad: 'MESSWERTE',
    fassung: 1,
    aktiv: true,
    sortOrder: 1,
    tabellen: { anziehprotokoll: 'AUS', vermessung: 'AUS' },
    punkte: startPunkte([
      ['hoehe', 'Maße', 'Geländerhöhe', 'MESSEN', { einheit: 'mm' }],
      ['oeffnung', 'Maße', 'Größte Öffnung der Füllung', 'MESSEN', { einheit: 'mm' }],
      ['unterkante', 'Maße', 'Abstand Unterkante zum Belag', 'MESSEN', { einheit: 'mm' }],
      ['duebel', 'Befestigung', 'Dübel (Typ, Setztiefe)', 'TEXT', { pflicht: true }],
      ['befestigung', 'Befestigung', 'Befestigung geprüft', 'PRUEFEN'],
      ['handlauf', 'Ausführung', 'Handlauf durchgehend', 'PRUEFEN'],
      ['oberflaeche', 'Ausführung', 'Oberfläche', 'PRUEFEN'],
      ['gesamtansicht', 'Ausführung', 'Foto Gesamtansicht', 'PRUEFEN', { fotoPflicht: true }],
    ]),
  },
  {
    id: '6f1d0a47-0003-4000-8000-000000000003',
    name: 'Stahlbau nach EN 1090-2',
    detailgrad: 'NACHWEIS',
    fassung: 1,
    aktiv: true,
    sortOrder: 2,
    tabellen: { anziehprotokoll: 'PFLICHT', vermessung: 'PFLICHT' },
    punkte: startPunkte([
      ['fussplatten', 'Auflager', 'Fußplatten unterstopft bzw. vergossen', 'PRUEFEN'],
      ['ankerschrauben', 'Auflager', 'Ankerschrauben', 'PRUEFEN'],
      ['nicht-vorgespannt', 'Verbindungen', 'Nicht vorgespannte Verbindungen handfest und vollständig', 'PRUEFEN'],
      ['vorgespannt', 'Verbindungen', 'Vorgespannte Verbindungen (siehe Anziehprotokoll)', 'PRUEFEN'],
      ['schweissung', 'Baustellenschweißung', 'Baustellenschweißung: Sichtprüfung', 'PRUEFEN'],
      ['schweisser', 'Baustellenschweißung', 'Schweißer', 'TEXT'],
      ['korrosionsschutz', 'Korrosionsschutz', 'Korrosionsschutz ausgebessert', 'PRUEFEN'],
      ['schichtdicke', 'Korrosionsschutz', 'Schichtdicke', 'MESSEN', { einheit: 'µm' }],
      ['lage', 'Lage', 'Lage, Höhe, Neigung (siehe Vermessung)', 'PRUEFEN'],
      ['montagehilfen', 'Abschluss', 'Montagehilfen entfernt', 'PRUEFEN'],
      ['montageanweisung', 'Abschluss', 'Montageanweisung eingehalten', 'PRUEFEN'],
    ]),
  },
]

// ── Mängelliste, Abnahme-Kandidaten, Schnittstelle (6.2, 3.1, 8.) ───────────

/** Eine Zeile der Mängelliste (6.2) — `GET /site-docs/assembly/defects`. */
export interface MontageMangelZeile {
  id: string
  documentId: string
  documentNumber: string | null
  documentStatus: string
  documentDate: string
  projectLabel: string
  projectId: string | null
  nr: number
  partKey: string | null
  partName: string | null
  text: string
  weight: MontageGewicht | null
  dueDate: string | null
  state: MontageMangelZustand
  resolvedAt: string | null
  resolvedNote: string | null
  resolvedByNumber: string | null
  projectTaskId: string | null
  /** „Aufgabe erledigt" als Hinweis neben dem offenen Mangel (6.3) */
  taskDoneAt: string | null
  updatedAt: string
}

/** Ein Montageprotokoll, auf das sich eine Abnahme beziehen kann (3.1). */
export interface MontageAbnahmeKandidat {
  documentId: string
  number: string
  contentHash: string
  documentDate: string
  umfang: MontageUmfang
  /** noch ohne Abnahme (Umfang Montage, keine freigegebene Abnahme bezieht sich darauf) */
  ohneAbnahme: boolean
  bauteile: { key: string; bezeichnung: string; exc: MontageExc | null }[]
  offeneMaengel: { nr: number; text: string; gewicht: MontageGewicht | null; bauteil: string | null }[]
}

/** Spalten des Mängel-Exports (6.2). */
export const MONTAGE_MAENGEL_SPALTEN: readonly { key: keyof MontageMangelZeile | 'stateText' | 'weightText'; label: string }[] = [
  { key: 'projectLabel', label: 'Projekt' },
  { key: 'documentNumber', label: 'Protokoll' },
  { key: 'documentDate', label: 'Datum' },
  { key: 'nr', label: 'Nr.' },
  { key: 'partName', label: 'Bauteil' },
  { key: 'text', label: 'Beschreibung' },
  { key: 'weightText', label: 'Gewicht' },
  { key: 'dueDate', label: 'Frist' },
  { key: 'stateText', label: 'Zustand' },
  { key: 'resolvedNote', label: 'Vermerk' },
]

// ── Diktat (13.) ────────────────────────────────────────────────────────────

/** Ein Mangel aus dem Diktat — Gewicht und Frist bleiben leer (MO-15). */
export interface MontageDiktatMangel {
  text: string
  /** Schlüssel des zugeordneten Bauteils — nur eindeutig, sonst „allgemein" (`null`). */
  bauteilKey: string | null
  /** Was die KI als Bauteil nannte. */
  bauteilGenannt: string | null
}

/**
 * Das Bauteil, das die KI nannte, gegen die Bezeichnungen des Protokolls — **nur eindeutig**, sonst
 * „allgemein" (`null`). Erst gleich (ohne Groß-/Kleinschreibung), dann „enthält" in eine Richtung.
 */
export function montageBauteilZuordnen(genannt: string | null | undefined, bauteile: readonly Pick<MontageBauteil, 'key' | 'bezeichnung'>[]): string | null {
  const g = (genannt ?? '').trim().toLocaleLowerCase('de')
  if (!g) return null
  const klein = (b: Pick<MontageBauteil, 'bezeichnung'>) => b.bezeichnung.trim().toLocaleLowerCase('de')
  const gleich = bauteile.filter((b) => klein(b) === g)
  if (gleich.length === 1) return gleich[0].key
  if (gleich.length > 1) return null
  const teil = bauteile.filter((b) => klein(b).includes(g) || (klein(b) && g.includes(klein(b))))
  return teil.length === 1 ? teil[0].key : null
}

/** Einen Mangel aus dem Diktat hinter die vorhandenen hängen — ohne Gewicht, ohne Frist. */
export function montageMangelAusDiktat(d: MontageDiktatMangel, nr: number): MontageMangel {
  return { nr, bauteilKey: d.bauteilKey, text: d.text, gewicht: null, frist: null, fotos: [] }
}

/** Die nächste freie Mangelnummer. */
export function montageNaechsteNr(maengel: readonly Pick<MontageMangel, 'nr'>[]): number {
  return maengel.reduce((n, m) => Math.max(n, m.nr), 0) + 1
}
